import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

function DashboardLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { logout } = useAuth();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  return (
    <div className="relative w-full h-screen bg-background-light">
      {/* Top App Bar - Fixed with safe area */}
      <header className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between border-b-2 border-black bg-background-light p-4" style={{ paddingTop: 'calc(var(--safe-area-inset-top) + 1rem)' }}>
        <h1 className="text-black text-xl font-bold">Shkalot</h1>
        <div className="relative" ref={menuRef}>
          <button 
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-black bg-white shadow-hard-sm hover:shadow-none hover:translate-x-0.5 hover:translate-y-0.5 transition-all"
          >
            <span className="text-2xl">👤</span>
          </button>
          
          {/* Dropdown Menu */}
          {showProfileMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white border-2 border-black shadow-hard rounded-lg overflow-hidden z-50">
              <button
                onClick={() => {
                  setShowProfileMenu(false);
                  navigate('/settings');
                }}
                className="w-full px-4 py-3 text-start font-bold hover:bg-gray-100 border-b-2 border-black flex items-center gap-2"
              >
                <span className="text-xl">⚙️</span>
                {t('nav.settings')}
              </button>
              <button
                onClick={() => {
                  setShowProfileMenu(false);
                  navigate('/ask');
                }}
                className="w-full px-4 py-3 text-start font-bold hover:bg-gray-100 border-b-2 border-black flex items-center gap-2"
              >
                <span className="text-xl">✨</span>
                {t('nav.askAI', 'Ask AI')}
              </button>
              <button
                onClick={() => {
                  setShowProfileMenu(false);
                  handleLogout();
                }}
                className="w-full px-4 py-3 text-start font-bold hover:bg-gray-100 flex items-center gap-2"
              >
                <span className="text-xl">🚪</span>
                {t('nav.logout', 'Logout')}
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content - Scrollable with padding for fixed header and nav */}
      <main className="h-full overflow-y-auto" style={{ paddingTop: 'calc(var(--safe-area-inset-top) + 72px)', paddingBottom: 'calc(var(--safe-area-inset-bottom) + 80px)' }}>
        {children}
      </main>

      {/* Bottom Navigation - Fixed with safe area */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-around border-t-2 border-black bg-background-light" style={{ height: 'calc(var(--safe-area-inset-bottom) + 80px)', paddingBottom: 'var(--safe-area-inset-bottom)' }}>
        <Link
          to="/dashboard"
          className={`flex flex-col items-center gap-1 transition-colors ${
            location.pathname === '/dashboard' ? 'text-accent' : 'text-black/60'
          }`}
        >
          <span className="text-3xl">🏠</span>
          <span className={`text-xs font-bold ${location.pathname === '/dashboard' ? 'text-accent' : ''}`}>
            {t('nav.dashboard')}
          </span>
        </Link>
        <Link
          to="/expenses"
          className={`flex flex-col items-center gap-1 transition-colors ${
            location.pathname === '/expenses' ? 'text-accent' : 'text-black/60'
          }`}
        >
          <span className="text-3xl">🕐</span>
          <span className={`text-xs font-bold ${location.pathname === '/expenses' ? 'text-accent' : ''}`}>
            {t('nav.history')}
          </span>
        </Link>
        <Link
          to="/analytics"
          className={`flex flex-col items-center gap-1 transition-colors ${
            location.pathname === '/analytics' ? 'text-accent' : 'text-black/60'
          }`}
        >
          <span className="text-3xl">📊</span>
          <span className={`text-xs font-bold ${location.pathname === '/analytics' ? 'text-accent' : ''}`}>
            {t('nav.analytics')}
          </span>
        </Link>
        <Link
          to="/settings"
          className={`flex flex-col items-center gap-1 transition-colors ${
            location.pathname === '/settings' ? 'text-accent' : 'text-black/60'
          }`}
        >
          <span className="text-3xl">⚙️</span>
          <span className={`text-xs font-bold ${location.pathname === '/settings' ? 'text-accent' : ''}`}>
            {t('nav.settings')}
          </span>
        </Link>
      </nav>
    </div>
  );
}

export default DashboardLayout;
